/**
 * Universal calendar event helpers.
 *
 * Browser apps cannot directly access Mac Calendar.app or Outlook desktop.
 * The portable approach is to either:
 *   - generate an `.ics` (iCalendar) file the user can double-click — opens in
 *     macOS Calendar, Outlook desktop, Outlook web, Google Calendar, etc.
 *   - generate a deep-link URL that opens the "compose event" form in Google
 *     Calendar or Outlook Web pre-filled with the event details.
 *
 * Neither requires OAuth or backend changes.
 */

export interface CalendarEvent {
  /** Event title shown in the calendar. */
  title: string;
  /** Free-form description / agenda. */
  description?: string;
  /** Optional location string. */
  location?: string;
  /** Event start as a JS Date. */
  start: Date;
  /** Event end as a JS Date. */
  end: Date;
  /** Comma- or array-separated attendee emails. */
  attendees?: string[];
}

/** Pads a number with a leading zero when it is a single digit. */
function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

/** Converts a Date to the UTC timestamp format required by iCal (`YYYYMMDDTHHMMSSZ`). */
function toIcsUtc(date: Date): string {
  return (
    `${date.getUTCFullYear()}` +
    `${pad2(date.getUTCMonth() + 1)}` +
    `${pad2(date.getUTCDate())}` +
    'T' +
    `${pad2(date.getUTCHours())}` +
    `${pad2(date.getUTCMinutes())}` +
    `${pad2(date.getUTCSeconds())}` +
    'Z'
  );
}

/** Escapes characters that have special meaning in iCalendar text values. */
function escapeIcs(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/\n/g, '\\n').replace(/,/g, '\\,').replace(/;/g, '\\;');
}

/**
 * Builds a fully-formed iCalendar (.ics) document for a single event.
 * Result can be passed to {@link downloadIcs} to trigger a browser download.
 *
 * @param event - Event details
 * @returns The .ics file body as a string
 */
export function buildIcs(event: CalendarEvent): string {
  const uid = `${Date.now()}-${Math.random().toString(36).slice(2)}@systemzero`;
  const dtStamp = toIcsUtc(new Date());
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//SystemZero//Command Bar//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${uid}`,
    `DTSTAMP:${dtStamp}`,
    `DTSTART:${toIcsUtc(event.start)}`,
    `DTEND:${toIcsUtc(event.end)}`,
    `SUMMARY:${escapeIcs(event.title)}`,
  ];

  if (event.description) lines.push(`DESCRIPTION:${escapeIcs(event.description)}`);
  if (event.location) lines.push(`LOCATION:${escapeIcs(event.location)}`);
  for (const attendee of event.attendees ?? []) {
    if (attendee.trim()) {
      lines.push(`ATTENDEE;CN=${escapeIcs(attendee)}:mailto:${attendee.trim()}`);
    }
  }

  lines.push('END:VEVENT', 'END:VCALENDAR');
  return lines.join('\r\n');
}

/** Triggers a browser download of an `.ics` file containing the given event. */
export function downloadIcs(event: CalendarEvent, filename = 'meeting.ics'): void {
  const ics = buildIcs(event);
  const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/** Encodes a date for Google Calendar render URLs (`YYYYMMDDTHHMMSSZ`). */
function toGoogleDate(date: Date): string {
  return toIcsUtc(date);
}

/**
 * Builds a Google Calendar "compose event" URL that opens a pre-filled new-event
 * form. Uses the user's existing Google session — no auth handled by us.
 */
export function buildGoogleCalendarUrl(event: CalendarEvent): string {
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: event.title,
    dates: `${toGoogleDate(event.start)}/${toGoogleDate(event.end)}`,
  });
  if (event.description) params.set('details', event.description);
  if (event.location) params.set('location', event.location);
  if (event.attendees && event.attendees.length > 0) {
    params.set('add', event.attendees.join(','));
  }
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

/**
 * Builds an Outlook Web "deeplink/compose" URL that opens a pre-filled
 * new-event form. Uses the user's Microsoft session — no auth handled by us.
 */
export function buildOutlookCalendarUrl(event: CalendarEvent): string {
  const params = new URLSearchParams({
    path: '/calendar/action/compose',
    rru: 'addevent',
    subject: event.title,
    startdt: event.start.toISOString(),
    enddt: event.end.toISOString(),
  });
  if (event.description) params.set('body', event.description);
  if (event.location) params.set('location', event.location);
  if (event.attendees && event.attendees.length > 0) {
    params.set('to', event.attendees.join(','));
  }
  return `https://outlook.live.com/owa/?${params.toString()}`;
}
